import type { Employee, OperationAttainment, Task } from 'types/interfaces';

export type RequiredAttainment = { id: number; label: string };

export interface WorkOrderTimeframe {
  start?: string | null;
  end?: string | null;
}

const toAttainmentId = (value: unknown): number =>
  typeof value === 'string' ? parseInt(value, 10) : Number(value);

const isValidAttainmentId = (id: number): boolean => !isNaN(id) && id > 0;

const startOfDay = (value: string): number => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const todayStart = (): number => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

export function isAttainmentValidForTimeframe(
  attainment: { startDate?: string; endDate?: string },
  timeframe?: WorkOrderTimeframe
): boolean {
  if (!attainment.endDate) return true;

  const windowStart = timeframe?.start ? startOfDay(timeframe.start) : todayStart();
  const windowEnd = timeframe?.end ? startOfDay(timeframe.end) : windowStart;

  if (startOfDay(attainment.endDate) < windowEnd) return false;

  if (attainment.startDate && startOfDay(attainment.startDate) > windowStart) {
    return false;
  }

  return true;
}

const collectAttainments = (
  operationAttainments: OperationAttainment[]
): RequiredAttainment[] => {
  const requiredAttainmentsMap = new Map<number, RequiredAttainment>();

  operationAttainments.forEach((opAtt) => {
    opAtt.attainments?.forEach((att) => {
      const id = toAttainmentId(att.id);
      if (isValidAttainmentId(id) && !requiredAttainmentsMap.has(id)) {
        requiredAttainmentsMap.set(id, {
          id,
          label: att.shortLabel || att.label || ''
        });
      }
    });
  });

  return Array.from(requiredAttainmentsMap.values());
};

export function attainmentsFromOperations(
  operationAttainments: OperationAttainment[] | undefined
): RequiredAttainment[] {
  if (!operationAttainments?.length) return [];
  return collectAttainments(operationAttainments);
}

export function attainmentsForOperation(
  operationAttainments: OperationAttainment[] | undefined,
  operationKey: string | number | undefined
): RequiredAttainment[] {
  if (!operationKey || !operationAttainments?.length) return [];

  const operationAtt = operationAttainments.find(
    (att) => att.operationType.id.toString() === operationKey.toString()
  );

  return operationAtt ? collectAttainments([operationAtt]) : [];
}

export function attainmentsFromTask(task: Task): RequiredAttainment[] {
  const candidates = [
    (task as any)?.requiredAttainments,
    (task as any)?.attainments,
    (task as any)?.skills,
    (task as any)?.requiredSkills
  ];
  const list = candidates.find((x) => Array.isArray(x)) as any[] | undefined;
  if (!list?.length) return [];

  const requiredAttainmentsMap = new Map<number, RequiredAttainment>();
  list.forEach((x: any) => {
    const id = toAttainmentId(x?.id ?? x?.attainmentId ?? x?.key);
    const label = x?.label ?? x?.shortLabel ?? x?.shortName ?? x?.name ?? '';
    if (isValidAttainmentId(id) && !requiredAttainmentsMap.has(id)) {
      requiredAttainmentsMap.set(id, { id, label: String(label) });
    }
  });

  return Array.from(requiredAttainmentsMap.values());
}

export function computeEmployeeAttainments(
  employee: Employee,
  requiredAttainments: RequiredAttainment[],
  timeframe?: WorkOrderTimeframe
): { availableAttainments: RequiredAttainment[]; unavailableAttainments: RequiredAttainment[] } {
  const availableAttainments: RequiredAttainment[] = [];
  const unavailableAttainments: RequiredAttainment[] = [];

  const employeeAttainments = new Map<number, { startDate?: string; endDate?: string }>();
  employee.attainments?.forEach((empAtt) => {
    const id = toAttainmentId(empAtt.attainmentId);
    if (isValidAttainmentId(id)) {
      employeeAttainments.set(id, {
        startDate: empAtt.startDate,
        endDate: empAtt.endDate
      });
    }
  });

  requiredAttainments.forEach((requiredAtt) => {
    const empAtt = employeeAttainments.get(requiredAtt.id);
    if (empAtt && isAttainmentValidForTimeframe(empAtt, timeframe)) {
      availableAttainments.push(requiredAtt);
    } else {
      unavailableAttainments.push(requiredAtt);
    }
  });

  return { availableAttainments, unavailableAttainments };
}

export function mapEmployeeAttainments(
  employees: Employee[],
  requiredAttainments: RequiredAttainment[],
  timeframe?: WorkOrderTimeframe
): Employee[] {
  if (!requiredAttainments.length) {
    return employees.map((employee) => ({
      ...employee,
      availableAttainments: [],
      unavailableAttainments: []
    }));
  }

  return employees.map((employee) => ({
    ...employee,
    ...computeEmployeeAttainments(employee, requiredAttainments, timeframe)
  }));
}
